// @ts-strict-ignore
import { type FormEvent, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { closeModal } from 'loot-core/client/actions';
import { createAccount } from 'loot-core/client/queries/queriesSlice';
import { SUPPORTED_CURRENCIES } from 'loot-core/src/shared/currencies';
import { type AccountType } from 'loot-core/types/models';
import { toRelaxedNumber } from 'loot-core/src/shared/util';

import * as useAccounts from '../../hooks/useAccounts';
import { useNavigate } from '../../hooks/useNavigate';
import { useSyncedPref } from '../../hooks/useSyncedPref';
import { useDispatch } from '../../redux';
import { Button } from '../ui/button2';
import { InitialFocus } from '../common/InitialFocus';
import { InlineField } from '../common/InlineField';
import { Input } from '../ui/input2';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '../ui/modal';
import { Select } from '../ui/select2';
import { View } from '../common/View';
import { validateAccountName } from '../util/accountValidation';
import { cn } from '../../lib/utils';

export function CreateLocalAccountModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accounts = useAccounts.useAccounts();
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('onbudget');
  const [balance, setBalance] = useState('0');
  const [defaultCurrency] = useSyncedPref('defaultCurrency');
  const [currency, setCurrency] = useState<string>(defaultCurrency || 'PEN');

  const [nameError, setNameError] = useState(null);
  const [balanceError, setBalanceError] = useState(false);

  const validateBalance = balance => !isNaN(parseFloat(balance));

  const validateAndSetName = (name: string) => {
    const nameError = validateAccountName(name, '', accounts);
    if (nameError) {
      setNameError(nameError);
    } else {
      setName(name);
      setNameError(null);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nameError = validateAccountName(name, '', accounts);

    const balanceError = !validateBalance(balance);
    setBalanceError(balanceError);

    if (!nameError && !balanceError) {
      dispatch(closeModal());
      const id = await dispatch(
        createAccount({
          name,
          balance: toRelaxedNumber(balance),
          accountType,
          currency,
        }),
      ).unwrap();
      navigate('/accounts/' + id);
    }
  };
  return (
    <Modal name="add-local-account">
      {({ state: { close } }) => (
        <>
          <ModalHeader
            title={
              <ModalTitle title={t('Create Local Account')} shrinkOnOverflow />
            }
            rightContent={<ModalCloseButton onClick={close} />}
          />
          <View>
            <form onSubmit={onSubmit}>
              <InlineField label="Name" width="100%">
                <InitialFocus>
                  <Input
                    name="name"
                    value={name}
                    onChange={event => setName(event.target.value)}
                    onBlur={event => {
                      const name = event.target.value.trim();
                      validateAndSetName(name);
                    }}
                    style={{ flex: 1 }}
                  />
                </InitialFocus>
              </InlineField>
              {nameError && (
                <View className="text-[var(--color-warningText)] text-[13px]" style={{ marginLeft: 75 }}>
                  {nameError}
                </View>
              )}

              <View className="w-full mt-3 mb-1">
                <div className="flex gap-2">
                  {(
                    [
                      {
                        value: 'onbudget' as AccountType,
                        label: t('On budget'),
                        description: t('Included in your budget'),
                      },
                      {
                        value: 'offbudget' as AccountType,
                        label: t('Off budget'),
                        description: t('Excluded from your budget'),
                      },
                      {
                        value: 'travel' as AccountType,
                        label: t('Travel'),
                        description: t('Dedicated travel tracking'),
                      },
                    ] as const
                  ).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAccountType(option.value)}
                      className={cn(
                        'flex-1 flex flex-col items-start gap-0.5 rounded-md border p-3 text-left text-sm transition-colors',
                        accountType === option.value
                          ? 'border-primary bg-primary/5 text-foreground'
                          : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs">{option.description}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('This cannot be changed later.')}
                </p>
              </View>

              <InlineField label="Balance" width="100%">
                <Input
                  name="balance"
                  inputMode="decimal"
                  value={balance}
                  onChange={event => setBalance(event.target.value)}
                  onBlur={event => {
                    const balance = event.target.value.trim();
                    setBalance(balance);
                    if (validateBalance(balance) && balanceError) {
                      setBalanceError(false);
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </InlineField>
              {balanceError && (
                <View className="text-destructive text-[13px]" style={{ marginLeft: 75 }}>
                  {t('Balance must be a number')}
                </View>
              )}

              <InlineField label="Currency" width="100%">
                <Select
                  value={currency}
                  onChange={setCurrency}
                  options={SUPPORTED_CURRENCIES}
                  style={{ flex: 1 }}
                />
              </InlineField>

              <ModalButtons>
                <Button onClick={close}>{t('Back')}</Button>
                <Button
                  type="submit"
                  variant="primary"
                  style={{ marginLeft: 10 }}
                >
                  {t('Create')}
                </Button>
              </ModalButtons>
            </form>
          </View>
        </>
      )}
    </Modal>
  );
}
